import { useEffect, useContext, useState } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { getBalanceRequestsOfUser } from '../api/balance-requests'
import { formatDate, formatAmount } from '../helpers/formatters'
import { Link } from 'react-router-dom'
import { generateNoticePDF } from '../helpers/createPdf'
import useMediaQuery from '../hooks/useMediaQuery'
import useAuth from '../hooks/useAuth'
import Loading from '../components/Loading/Loading'
import BoxSelectButton from '../components/TableButtons/BoxSelectButton'
import StatusText from '../components/StatusText/StatusText'
import PopupMessage from '../components/PopupMessage/PopupMessage'
import HistoryItem from '../components/mobile-components/HistoryItem/HistoryItem'
import HistoryTable from '../components/HistoryTable/HistoryTable'
import Modal from '../components/Modal/Modal'
import Header from '../components/Header/Header'

const BalanceRequestDebts = () => {
    const { token } = useContext(AuthContext)
    const { getCurrentUserID } = useAuth()

    useEffect(() => {
        document.title =
            'Οφειλές από αιτήσεις υπολοίπου - Πλατφόρμα Ηλ. Πληρωμών Δήμου Καρπάθου'
    }, [])

    const [popup, setPopup] = useState({ message: '', type: '', shown: false })
    const [debtsToPay, setDebtsToPay] = useState({
        totalAmount: 0,
        debtsId: [],
    })
    const [loadingForPay, setLoadingForPay] = useState(false)
    const isMobile = useMediaQuery('(max-width:1024px)')
    const userId = getCurrentUserID()
    const options = {
        headers: {
            Authorization: `Bearer ${token.access_token}`,
        },
    }

    const { isLoading, data } = useQuery({
        queryKey: ['balanceRequests', userId, options],
        queryFn: () => getBalanceRequestsOfUser(userId, options),
    })

    if (isLoading || loadingForPay) {
        return <Loading />
    }

    const { profile, balanceRequests } = data

    const balanceRequestDebts =
        data &&
        balanceRequests &&
        balanceRequests
            .filter(
                request => request.govpayDebts && request.govpayDebts.length > 0
            ) // Ensure govpayDebts exists
            .map(request => request.govpayDebts)
            .flat()

    const headings = [
        '',
        'Κωδικός Πληρωμής',
        'Κωδικός Govpay',
        'Αιτιολογία',
        'Ποσό',
        'Ποσό ολογράφως',
        'Καταληκτική ημ/νία πληρωμής χωρίς προσαύξηση',
        'Κατάσταση',
    ]

    const rows =
        data && balanceRequestDebts?.length
            ? balanceRequestDebts.map(debt => {
                  return {
                      0: (
                          <BoxSelectButton
                              debtId={debt.id}
                              debtTotalAmount={debt.amount}
                              debtsToPay={debtsToPay}
                              setDebtsToPay={setDebtsToPay}
                          />
                      ),
                      1: debt.paymentCode ?? '-',
                      2: debt.systemCode ?? '-',
                      3: debt.description ?? '-',
                      4: formatAmount(debt.amount) ?? '-',
                      5: debt.amountString ?? '-',
                      6: formatDate(debt.expirationDate) ?? '-',
                      7: <StatusText kind="payment" status={debt.status} />,
                  }
              })
            : []

    const submitHandler = () => {
        setLoadingForPay(true)

        setTimeout(() => {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Η διαδικασία πληρωμής ακυρώθηκε.</p>
                        <p>Παρακαλώ προσπαθήστε ξανά.</p>
                    </>
                ),
                type: 'errorPayment',
            }))

            setLoadingForPay(false)
        }, 3000)
    }

    const handleNoticeClick = async () => {
        if (debtsToPay.debtsId.length === 0) {
            setPopup({
                shown: true,
                type: 'error',
                message: 'Παρακαλώ επιλέξτε οφειλές',
            })

            return
        }

        const selectedDebts = balanceRequestDebts
            ? balanceRequestDebts.filter(debt =>
                  debtsToPay.debtsId.includes(debt.id)
              )
            : []

        await generateNoticePDF(selectedDebts, profile)
    }

    return (
        <div className="flex flex-col">
            {popup.shown && (
                <PopupMessage
                    message={popup.message}
                    type={popup.type}
                    closePopUp={() =>
                        setPopup(values => ({ ...values, shown: false }))
                    }
                    showCloseButton={true}
                />
            )}

            {isMobile ? (
                <>
                    <h2 className="text-primary text-2xl text-center my-8">
                        Οφειλές από Αίτηση Υπολοίπου
                    </h2>

                    {balanceRequestDebts && balanceRequestDebts.length > 0 ? (
                        balanceRequestDebts.map((debt, index) => {
                            return (
                                <HistoryItem
                                    key={debt.id}
                                    headings={headings}
                                    rows={rows[index]}
                                />
                            )
                        })
                    ) : (
                        <h2 className="text-base text-primary font-bold text-center">
                            Δεν βρέθηκαν οφειλές
                        </h2>
                    )}
                </>
            ) : (
                <Modal classes="w-full  max-w-7xl flex flex-col">
                    <Header
                        includeLogo={true}
                        withBorder={true}
                        includeButtons={true}
                    />

                    <h2 className="text-primary text-3xl font-light mt-12 mb-6 self-center">
                        Οφειλές από Αίτηση Υπολοίπου
                    </h2>

                    <div className="overflow-auto">
                        <div className="w-[80%] lg:w-full flex flex-col justify-start items-center grow">
                            <HistoryTable
                                headings={headings}
                                rows={rows}
                                noDataText={
                                    <>
                                        <div>
                                            Παρακαλούμε χρησιμοποιήστε τη
                                            διαδικασία&nbsp;
                                            <Link
                                                className="font-bold text-olive"
                                                to={'/balance-request-debts'}
                                            >
                                                αίτησης υπολοίπου&nbsp;
                                            </Link>
                                            για να ενημερωθείτε για τις οφειλές
                                            σας.
                                        </div>
                                    </>
                                }
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {balanceRequestDebts && balanceRequestDebts.length > 0 ? (
                <div className="flex flex-wrap items-center justify-end gap-4 mt-10">
                    <button
                        className="bg-primary text-white border border-primary hover:bg-white hover:text-primary transition-all rounded-lg w-fit py-3.5 px-10"
                        onClick={handleNoticeClick}
                    >
                        Έκδοση ειδοποιητηρίου
                    </button>

                    <button
                        className="bg-primary text-white border border-primary hover:bg-white hover:text-primary transition-all rounded-lg w-fit py-3.5 px-10"
                        onClick={submitHandler}
                    >
                        {`Πληρωμή 
                            ${
                                debtsToPay.totalAmount !== 0
                                    ? debtsToPay.totalAmount + ' €'
                                    : ''
                            }`}
                    </button>
                </div>
            ) : null}
        </div>
    )
}

export default BalanceRequestDebts
